import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { formatSubscriptionDate } from 'utils/dates';
import {
  getBillingPortalUrl,
  getCardExpiryParts,
  getLastInvoiceMeta,
  getLastInvoicePdfUrl,
  getPaymentMethodSummary,
} from 'utils/subscriptionBilling';
import {
  ContentWrapper,
  Loader,
  PopupButtonMessage,
  PopupEventWrapper,
} from 'components';
import { ROUTES, PROFILE_ROUTES } from 'configs';
import {
  selectCurrentUserSubscription,
  selectIsSubscriptionLoading,
} from 'store/selectors/subscriptions';
import { getAuthUser } from 'store/selectors/auth';
import { getCommunities } from 'store/selectors/communities';
import {
  startTrial,
  activateSubscription,
  getCurrentUserSubscription,
} from 'store/actionCreators/subscriptions';
import {
  SubscriptionFrequency,
  SubscriptionStatus,
  UserSubscription,
} from 'types';

import Lottie from 'react-lottie';
import warningSign from 'static/animations/warning-sign.json';

import { StripeWordmark } from 'pages/Subscriptions/SubscriptionInner/PaymentBrandIcons';

import StripeCheckoutOutcome from './StripeCheckoutOutcome';

import {
  ActionRow,
  Badge,
  BarFill,
  BarTrack,
  BtnOutline,
  BtnPrimary,
  Card,
  CardHeaderRow,
  CardSubtitle,
  CardTitle,
  DropdownItem,
  DropdownPanel,
  FeatureBlurb,
  FeatureBox,
  FeatureItem,
  FeatureList,
  FloatingLoaderWrap,
  FooterNote,
  HeroAccent,
  HeroActions,
  HeroCard,
  HeroInner,
  HeroLeft,
  HeroSubtitle,
  HeroTitle,
  IconBox,
  InfoNote,
  Main,
  PageRoot,
  PaymentBlock,
  PaymentIconWrap,
  PaymentLabel,
  PaymentRow,
  PaymentValue,
  PlanFooter,
  PlanName,
  SmallHint,
  ChoosePlanFullButton,
  SplitButtonChevron,
  SplitButtonMain,
  SplitButtonWrap,
  StatusBanner,
  StatusBannerBody,
  StatusBannerTitle,
  TextLink,
  TopBar,
  TopBarTitle,
  TrialBanner,
  TrialBannerContent,
  TrialBannerIconWrap,
  TrialBannerText,
  TrialBannerTitle,
  FooterStripeNote,
  TrustRow,
  TwoColGrid,
  UsageCard,
  UsageFraction,
  UsageFractionMuted,
  UsageGrid,
  UsageHeader,
  UsageHint,
  UsageHintSmall,
  UsageHintWarn,
  UsageLabel,
  UsageMetric,
  UsageMetricTop,
  UsageUpgradeBtn,
  UsageUpgradeNudge,
  UsageUpgradeNudgeText,
  StatusBannerMain,
  StatusCheckWrap,
  StatusBannerTextCol,
  FrequencyPill,
} from './styled';

const TRIAL_DEFAULTS = {
  maxCommunities: 2,
  maxGroups: 40,
  maxMembers: 200,
  maxStorage: 4,
} as const;

const pct = (used: number, max: number) => {
  if (!max || max <= 0) return 0;
  return Math.min(100, Math.round((used / max) * 100));
};

const formatMoney = (currency: string | undefined, value: number) => {
  const sym = currency === 'EUR' ? '€' : currency === 'CHF' ? 'CHF ' : '$';
  return `${sym}${Number.isInteger(value) ? value : value.toFixed(2)}`;
};

const IconCheck = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
    <path
      fill="#00a4a6"
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
    />
  </svg>
);

const IconRocket = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M12 2.5s4 5.25 4 10.25a8 8 0 01-1.5 4.5L12 22l-2.5-4.75A8 8 0 018 12.75C8 7.75 12 2.5 12 2.5zm0 5.25a1.75 1.75 0 100 3.5 1.75 1.75 0 000-3.5zM5 15l-2 4.5V15h2zm14 0h2v4.5L19 15z" />
  </svg>
);

const IconInfo = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="#f5a623" aria-hidden>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
  </svg>
);

/** Same Lottie as `InfoPopup` / `PopupButtonMessage` start-trial flow (`type="warning"`) */
const TRIAL_BANNER_LOTTIE_OPTIONS = {
  loop: true,
  autoplay: true,
  animationData: warningSign,
};

const TrialBannerWarningLottie = () => (
  <TrialBannerIconWrap aria-hidden>
    <Lottie
      options={TRIAL_BANNER_LOTTIE_OPTIONS}
      width={44}
      height={44}
      isClickToPauseDisabled
    />
  </TrialBannerIconWrap>
);

const IconLock = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
  </svg>
);

const IconCalendar = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z" />
  </svg>
);

const IconOpen = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" />
  </svg>
);

const IconChevronDown = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M7 10l5 5 5-5z" />
  </svg>
);

const IconChevronRight = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6-6-6z" />
  </svg>
);

const UserProfileSubscription = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const subscription = useSelector(selectCurrentUserSubscription);
  const profile = useSelector(getAuthUser);
  const communities = useSelector(getCommunities);
  const isLoading = useSelector(selectIsSubscriptionLoading);
  const [planMenuOpen, setPlanMenuOpen] = useState(false);
  const planMenuRef = useRef<HTMLDivElement>(null);

  const changePlanPath = `${ROUTES.PROFILE}/${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.CHANGE_PLAN}`;

  const stripeOutcome = useMemo(() => {
    if (searchParams.get('stripe_success') === '1') return 'success' as const;
    if (
      searchParams.get('stripe_cancel') === '1' ||
      searchParams.get('canceled') === 'true'
    ) {
      return 'failure' as const;
    }
    return null;
  }, [searchParams]);

  const clearStripeQuery = useCallback(() => {
    navigate({ pathname: location.pathname, search: '' }, { replace: true });
  }, [navigate, location.pathname]);

  useEffect(() => {
    if (window) {
      window.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    dispatch(getCurrentUserSubscription.trigger());
  }, [dispatch]);

  useEffect(() => {
    if (!planMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (
        planMenuRef.current &&
        !planMenuRef.current.contains(e.target as Node)
      ) {
        setPlanMenuOpen(false);
      }
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [planMenuOpen]);

  const groupsCount = useMemo(() => {
    if (communities.length > 0 && profile) {
      const owned = communities.filter(
        (item) => profile?.userId === item?.communityOwner?.userId,
      );
      return owned.reduce(
        (acc, item) => acc + (Number(item.groupsCount) || 0),
        0,
      );
    }
    return 0;
  }, [communities, profile]);

  const subscriptionEndDate = useMemo(() => {
    const sub = subscription as UserSubscription | null | undefined;
    if (sub?.stripeCurrentPeriodEnd) {
      return formatSubscriptionDate(sub.stripeCurrentPeriodEnd, i18n.language);
    }
    if (!subscription?.endDate) return '—';
    return formatSubscriptionDate(subscription.endDate, i18n.language);
  }, [subscription, i18n.language]);

  const translatedPaymentCycle = useMemo(() => {
    if (subscription?.frequency === SubscriptionFrequency.MONTH) {
      return t('profileSubscription.details.paymentCycleMonth');
    }
    if (subscription?.frequency === SubscriptionFrequency.YEAR) {
      return t('profileSubscription.details.paymentCycleYear');
    }
    return '';
  }, [subscription, t]);

  const nextPaymentLabel = useMemo(() => {
    if (
      (subscription?.status === SubscriptionStatus.CANCELLED &&
        subscription?.subscriptionId !== SubscriptionStatus.MIGRATED) ||
      subscription?.planId === SubscriptionStatus.TRIAL
    ) {
      return t('profileSubscription.details.validUntil');
    }
    return t('profileSubscription.details.nextPayment');
  }, [subscription, t]);

  const planName = useMemo(() => {
    if (subscription?.id) {
      if (
        subscription.status === SubscriptionStatus.CANCELLED &&
        subscription.subscriptionId !== SubscriptionStatus.MIGRATED
      ) {
        return t('subscriptions.cancelledTitle');
      }
      if (subscription.planId === SubscriptionStatus.TRIAL) {
        return t('subscriptions.trial');
      }
      if (subscription.subscriptionPlan) {
        return subscription.subscriptionPlan.productName;
      }
      return subscription.planId;
    }
    return t('subscriptions.free');
  }, [subscription, t]);

  const isCancelled =
    subscription?.status === SubscriptionStatus.CANCELLED &&
    subscription?.subscriptionId !== SubscriptionStatus.MIGRATED;

  const isTrial =
    Boolean(subscription?.id) &&
    subscription?.planId === SubscriptionStatus.TRIAL;

  const isPaidActive =
    Boolean(subscription?.id) &&
    subscription?.planId !== SubscriptionStatus.TRIAL &&
    !isCancelled;

  const enterpriseAccountManagerName =
    process.env.REACT_APP_ENTERPRISE_ACCOUNT_MANAGER_NAME || '';
  const enterpriseAccountManagerEmail =
    process.env.REACT_APP_ENTERPRISE_ACCOUNT_MANAGER_EMAIL || '';

  const isInvoiceBillingLayout = useMemo(() => {
    if (!isPaidActive) return false;
    const sub = subscription as UserSubscription | null | undefined;
    if (!sub) return false;
    if (sub.stripeCollectionMethod === 'send_invoice') return true;
    if (sub.enterpriseInvoiceRequest?.contactEmail) return true;
    return false;
  }, [isPaidActive, subscription]);

  const billingNextLabel = useMemo(() => {
    const sub = subscription as UserSubscription | null | undefined;
    if (sub?.stripeCollectionMethod === 'send_invoice') {
      return t('profileSubscription.billing.nextInvoice');
    }
    return nextPaymentLabel;
  }, [subscription, nextPaymentLabel, t]);

  const billingCyclePrimaryText = useMemo(() => {
    const sub = subscription as UserSubscription | null | undefined;
    if (!sub || !isPaidActive) return '';
    if (
      sub.stripePriceUnitAmount != null &&
      sub.stripePriceCurrency &&
      sub.stripePriceInterval
    ) {
      const per =
        sub.stripePriceInterval === 'year'
          ? t('changePlan.perYear').trim()
          : sub.stripePriceInterval === 'month'
          ? t('changePlan.perMonth').trim()
          : '';
      const cycle =
        sub.stripePriceInterval === 'year'
          ? t('profileSubscription.details.paymentCycleYear')
          : sub.stripePriceInterval === 'month'
          ? t('profileSubscription.details.paymentCycleMonth')
          : translatedPaymentCycle;
      return `${cycle} · ${formatMoney(
        sub.stripePriceCurrency,
        sub.stripePriceUnitAmount,
      )}${per ? ` ${per}` : ''}`;
    }
    if (isInvoiceBillingLayout) {
      return t('profileSubscription.billing.invoiceBillingCyclePrimary');
    }
    if (translatedPaymentCycle && sub.subscriptionPlan) {
      return `${translatedPaymentCycle} · ${formatMoney(
        sub.subscriptionPlan.currency,
        sub.frequency === SubscriptionFrequency.YEAR
          ? sub.subscriptionPlan.yearlyValue
          : sub.subscriptionPlan.monthlyValue,
      )}${
        sub.frequency === SubscriptionFrequency.YEAR
          ? ` ${t('changePlan.perYear').trim()}`
          : ` ${t('changePlan.perMonth').trim()}`
      }`;
    }
    return translatedPaymentCycle || '';
  }, [
    subscription,
    isPaidActive,
    isInvoiceBillingLayout,
    translatedPaymentCycle,
    t,
  ]);

  const invoiceContactEmail = useMemo(() => {
    const sub = subscription as UserSubscription | null | undefined;
    if (!sub) return undefined;
    if (sub.enterpriseInvoiceRequest?.contactEmail) {
      return sub.enterpriseInvoiceRequest.contactEmail;
    }
    if (Array.isArray(sub.payerEmails) && sub.payerEmails[0]) {
      return sub.payerEmails[0];
    }
    return undefined;
  }, [subscription]);

  const maxBucketSize = subscription?.maxStorage || 0;
  const maxCommunities = subscription?.maxCommunities || 0;
  const maxGroups = subscription?.maxGroups || 0;
  const maxUsers = subscription?.maxMembers || 0;

  const checkoutPlanLabel = useMemo(
    () =>
      subscription?.subscriptionPlan?.productName ||
      subscription?.subscriptionPlan?.name,
    [subscription?.subscriptionPlan],
  );

  const paymentTrustFooter = (
    // <FooterPaymentTrustCard>
    <FooterStripeNote>
      <IconLock size={16} />
      <span>{t('profileSubscription.billing.poweredBy')}</span>
      <StripeWordmark height={16} />
    </FooterStripeNote>
    // </FooterPaymentTrustCard>
  );

  const handleTrialStart = () => {
    dispatch(startTrial());
  };

  const TrialStartButton = () => (
    <BtnPrimary type="button">{t('subscriptions.startTrial')}</BtnPrimary>
  );

  const showInitialLoader = isLoading && !subscription?.id;

  const billingPortalUrl = getBillingPortalUrl(subscription ?? undefined);
  const canUseManageBilling = Boolean(billingPortalUrl) && !isTrial;
  const paymentMethodLine = getPaymentMethodSummary(subscription ?? undefined);
  const cardExpiry = getCardExpiryParts(subscription ?? undefined);
  const lastInvoiceMeta = getLastInvoiceMeta(subscription ?? undefined);
  const lastInvoicePdfUrl = getLastInvoicePdfUrl(subscription ?? undefined);

  const paymentColumn = useMemo(
    () => (
      <Card>
        <CardTitle style={{ fontSize: '18px', marginBottom: '24px' }}>
          {t('profileSubscription.billing.paymentTitle')}
        </CardTitle>
        <PaymentBlock>
          {isInvoiceBillingLayout ? (
            <>
              <PaymentRow>
                <PaymentIconWrap>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="#94a3b8"
                    aria-hidden
                  >
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                  </svg>
                </PaymentIconWrap>
                <div>
                  <PaymentLabel>
                    {t('profileSubscription.billing.paymentMethodLabel')}
                  </PaymentLabel>
                  <PaymentValue>
                    {t('profileSubscription.billing.invoicePaymentMethodTitle')}
                  </PaymentValue>
                  {invoiceContactEmail ? (
                    <p
                      style={{
                        margin: '6px 0 0',
                        fontSize: 11,
                        color: '#64748b',
                        fontWeight: 500,
                      }}
                    >
                      {t('profileSubscription.billing.sentToBilling', {
                        email: invoiceContactEmail,
                      })}
                    </p>
                  ) : null}
                </div>
              </PaymentRow>
              <PaymentRow>
                <PaymentIconWrap>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="#94a3b8"
                    aria-hidden
                  >
                    <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z" />
                  </svg>
                </PaymentIconWrap>
                <div>
                  <PaymentLabel>
                    {t('profileSubscription.billing.billingCycleLabel')}
                  </PaymentLabel>
                  <PaymentValue>{billingCyclePrimaryText}</PaymentValue>
                  <p
                    style={{
                      margin: '10px 0 0',
                      fontSize: 11,
                      color: '#64748b',
                      fontWeight: 500,
                    }}
                  >
                    {billingNextLabel}{' '}
                    <span style={{ color: '#191c1e', fontWeight: 700 }}>
                      {subscriptionEndDate}
                    </span>
                  </p>
                </div>
              </PaymentRow>
              {enterpriseAccountManagerName || enterpriseAccountManagerEmail ? (
                <PaymentRow>
                  <PaymentIconWrap>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="#94a3b8"
                      aria-hidden
                    >
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                  </PaymentIconWrap>
                  <div>
                    <PaymentLabel>
                      {t('profileSubscription.billing.accountManagerLabel')}
                    </PaymentLabel>
                    <PaymentValue>
                      {enterpriseAccountManagerName || '—'}
                    </PaymentValue>
                    {enterpriseAccountManagerEmail ? (
                      <TextLink
                        href={`mailto:${enterpriseAccountManagerEmail}`}
                        style={{
                          fontSize: 11,
                          marginTop: 6,
                          display: 'inline-block',
                        }}
                      >
                        {enterpriseAccountManagerEmail}
                      </TextLink>
                    ) : null}
                  </div>
                </PaymentRow>
              ) : null}
              <PaymentRow>
                <PaymentIconWrap>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="#94a3b8"
                    aria-hidden
                  >
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                  </svg>
                </PaymentIconWrap>
                <div>
                  <PaymentLabel>
                    {t('profileSubscription.billing.lastInvoiceLabel')}
                  </PaymentLabel>
                  <PaymentValue style={{ fontSize: 13 }}>
                    {lastInvoiceMeta
                      ? `${formatMoney(
                          lastInvoiceMeta.currency,
                          lastInvoiceMeta.amount,
                        )} · ${formatSubscriptionDate(
                          lastInvoiceMeta.paidAt,
                          i18n.language,
                        )}`
                      : t('profileSubscription.billing.lastInvoiceUnavailable')}
                  </PaymentValue>
                  <p
                    style={{
                      margin: '6px 0 0',
                      fontSize: 11,
                      color: '#64748b',
                      fontWeight: 500,
                    }}
                  >
                    {t('profileSubscription.billing.lastInvoiceHintInvoice')}
                  </p>
                  {lastInvoicePdfUrl || billingPortalUrl ? (
                    <TextLink
                      href={lastInvoicePdfUrl || billingPortalUrl || '#'}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        fontSize: 11,
                        marginTop: 4,
                        display: 'inline-block',
                      }}
                    >
                      {t('profileSubscription.billing.downloadInvoice')}
                    </TextLink>
                  ) : null}
                </div>
              </PaymentRow>
            </>
          ) : (
            <>
              <PaymentRow>
                <PaymentIconWrap>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="#94a3b8"
                    aria-hidden
                  >
                    <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
                  </svg>
                </PaymentIconWrap>
                <div>
                  <PaymentLabel>
                    {t('profileSubscription.billing.paymentMethodLabel')}
                  </PaymentLabel>
                  <PaymentValue>
                    {isPaidActive
                      ? paymentMethodLine ||
                        t('profileSubscription.billing.paymentMethodOnFile')
                      : t('profileSubscription.billing.noPaymentYet')}
                  </PaymentValue>
                  {isPaidActive && cardExpiry ? (
                    <p
                      style={{
                        margin: '6px 0 0',
                        fontSize: 11,
                        color: '#64748b',
                        fontWeight: 500,
                      }}
                    >
                      {t('profileSubscription.billing.cardExpiresShort', {
                        month: String(cardExpiry.month).padStart(2, '0'),
                        year: String(cardExpiry.year),
                      })}
                    </p>
                  ) : null}
                </div>
              </PaymentRow>
              <PaymentRow>
                <PaymentIconWrap>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="#94a3b8"
                    aria-hidden
                  >
                    <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z" />
                  </svg>
                </PaymentIconWrap>
                <div>
                  <PaymentLabel>
                    {t('profileSubscription.billing.billingCycleLabel')}
                  </PaymentLabel>
                  <PaymentValue>
                    {isPaidActive && billingCyclePrimaryText
                      ? billingCyclePrimaryText
                      : isPaidActive && translatedPaymentCycle
                      ? translatedPaymentCycle
                      : t('profileSubscription.billing.noActiveSubscription')}
                  </PaymentValue>
                  {isPaidActive ? (
                    <p
                      style={{
                        margin: '10px 0 0',
                        fontSize: 11,
                        color: '#64748b',
                        fontWeight: 500,
                      }}
                    >
                      {billingNextLabel}{' '}
                      <span style={{ color: '#191c1e', fontWeight: 700 }}>
                        {subscriptionEndDate}
                      </span>
                    </p>
                  ) : null}
                </div>
              </PaymentRow>
              {isPaidActive ? (
                <PaymentRow>
                  <PaymentIconWrap>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="#94a3b8"
                      aria-hidden
                    >
                      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                    </svg>
                  </PaymentIconWrap>
                  <div>
                    <PaymentLabel>
                      {t('profileSubscription.billing.lastInvoiceLabel')}
                    </PaymentLabel>
                    <PaymentValue style={{ fontSize: 13 }}>
                      {lastInvoiceMeta
                        ? `${formatMoney(
                            lastInvoiceMeta.currency,
                            lastInvoiceMeta.amount,
                          )} · ${formatSubscriptionDate(
                            lastInvoiceMeta.paidAt,
                            i18n.language,
                          )}`
                        : t(
                            'profileSubscription.billing.lastInvoiceUnavailable',
                          )}
                    </PaymentValue>
                    <p
                      style={{
                        margin: '6px 0 0',
                        fontSize: 11,
                        color: '#64748b',
                        fontWeight: 500,
                      }}
                    >
                      {t('profileSubscription.billing.lastInvoiceHint')}
                    </p>
                    {lastInvoicePdfUrl || billingPortalUrl ? (
                      <TextLink
                        href={lastInvoicePdfUrl || billingPortalUrl || '#'}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          fontSize: 11,
                          marginTop: 4,
                          display: 'inline-block',
                        }}
                      >
                        {t('profileSubscription.billing.downloadInvoice')}
                      </TextLink>
                    ) : null}
                  </div>
                </PaymentRow>
              ) : null}
            </>
          )}
          {!isPaidActive && (
            <InfoNote>
              {t('profileSubscription.billing.paymentInfoNote')}
            </InfoNote>
          )}
        </PaymentBlock>
        {billingPortalUrl || isTrial ? (
          <div
            style={{
              marginTop: 'auto',
              paddingTop: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {canUseManageBilling ? (
              <BtnOutline
                href={billingPortalUrl}
                target="_blank"
                rel="noreferrer"
              >
                <IconOpen />
                {t('profileSubscription.billing.manageBilling')}
              </BtnOutline>
            ) : (
              <BtnOutline
                as="button"
                type="button"
                disabled
                style={{
                  opacity: 0.65,
                  cursor: 'not-allowed',
                  pointerEvents: 'none',
                }}
              >
                <IconOpen />
                {t('profileSubscription.billing.manageBilling')}
              </BtnOutline>
            )}
            <SmallHint style={{ marginTop: 0 }}>
              <IconLock size={13} />
              {t('profileSubscription.billing.manageBillingHint')}
            </SmallHint>
          </div>
        ) : null}
      </Card>
    ),
    [
      billingCyclePrimaryText,
      billingNextLabel,
      billingPortalUrl,
      canUseManageBilling,
      cardExpiry,
      i18n.language,
      invoiceContactEmail,
      isInvoiceBillingLayout,
      isPaidActive,
      isTrial,
      lastInvoiceMeta,
      lastInvoicePdfUrl,
      paymentMethodLine,
      subscription,
      subscriptionEndDate,
      t,
      translatedPaymentCycle,
    ],
  );

  const usageSection = (locked: boolean, showUsageGuidance = false) => {
    const u = subscription?.info;
    const m = locked ? 0 : maxUsers;
    const c = locked ? 0 : maxCommunities;
    const g = locked ? 0 : maxGroups;
    const s = locked ? 0 : maxBucketSize;
    const usedMembers = locked ? 0 : u?.users ?? 0;
    const usedCommunities = locked ? 0 : u?.communities ?? 0;
    const usedGroups = locked ? 0 : groupsCount;
    const usedStorage = locked ? 0 : u?.bucketSize ?? 0;

    const membersAtLimit =
      showUsageGuidance && !locked && m > 0 && usedMembers >= m;
    const commAtLimit =
      showUsageGuidance && !locked && c > 0 && usedCommunities >= c;
    const schedAtLimit =
      showUsageGuidance && !locked && g > 0 && usedGroups >= g;
    const storageAtLimit =
      showUsageGuidance && !locked && s > 0 && usedStorage >= s - 0.0001;
    const anyAtLimit =
      membersAtLimit || commAtLimit || schedAtLimit || storageAtLimit;

    const membersHigh =
      showUsageGuidance &&
      m > 0 &&
      pct(usedMembers, m) >= 70 &&
      !membersAtLimit;

    const onlyCommunitiesAtLimit =
      commAtLimit && !membersAtLimit && !schedAtLimit && !storageAtLimit;

    return (
      <UsageCard>
        <UsageHeader>
          <CardTitle style={{ fontSize: '18px', margin: 0 }}>
            {t('profileSubscription.billing.usageTitle')}
          </CardTitle>
          {locked && (
            <UsageHint>
              <IconLock size={14} />
              {t('profileSubscription.billing.usageLockedHint')}
            </UsageHint>
          )}
        </UsageHeader>
        <UsageGrid $dimmed={locked}>
          <UsageMetric>
            <UsageMetricTop>
              <UsageLabel>
                {t('profileSubscription.billing.usageMembers')}
              </UsageLabel>
              {locked ? (
                <UsageFractionMuted>0 / —</UsageFractionMuted>
              ) : (
                <UsageFraction
                  $tone={
                    membersAtLimit ? 'amber' : membersHigh ? 'amber' : 'default'
                  }
                >
                  {usedMembers} / {m || '—'}
                </UsageFraction>
              )}
            </UsageMetricTop>
            <BarTrack>
              {!locked && m > 0 ? (
                <BarFill
                  $pct={pct(usedMembers, m)}
                  $tone={
                    membersAtLimit ? 'amber' : membersHigh ? 'amber' : 'default'
                  }
                />
              ) : null}
            </BarTrack>
            {showUsageGuidance && !locked && m > 0 ? (
              membersAtLimit ? (
                <UsageHintWarn>
                  {t('profileSubscription.billing.usageAtLimitMembers')}
                </UsageHintWarn>
              ) : (
                <UsageHintSmall>
                  {t('profileSubscription.billing.usageSpotsRemaining', {
                    count: Math.max(0, m - usedMembers),
                  })}
                </UsageHintSmall>
              )
            ) : null}
          </UsageMetric>
          <UsageMetric>
            <UsageMetricTop>
              <UsageLabel>
                {t('profileSubscription.billing.usageCommunities')}
              </UsageLabel>
              {locked ? (
                <UsageFractionMuted>0 / —</UsageFractionMuted>
              ) : (
                <UsageFraction $tone={commAtLimit ? 'amber' : 'default'}>
                  {usedCommunities} / {c || '—'}
                </UsageFraction>
              )}
            </UsageMetricTop>
            <BarTrack>
              {!locked && c > 0 ? (
                <BarFill
                  $pct={pct(usedCommunities, c)}
                  $tone={commAtLimit ? 'amber' : 'default'}
                />
              ) : null}
            </BarTrack>
            {showUsageGuidance && !locked && c > 0 ? (
              commAtLimit ? (
                <UsageHintWarn>
                  {t('profileSubscription.billing.usageAtLimitCommunities')}
                </UsageHintWarn>
              ) : (
                <UsageHintSmall>
                  {t('profileSubscription.billing.usageCommunitiesRemaining', {
                    count: Math.max(0, c - usedCommunities),
                  })}
                </UsageHintSmall>
              )
            ) : null}
          </UsageMetric>
          <UsageMetric>
            <UsageMetricTop>
              <UsageLabel>
                {t('profileSubscription.billing.usageSchedulers')}
              </UsageLabel>
              {locked ? (
                <UsageFractionMuted>0 / —</UsageFractionMuted>
              ) : (
                <UsageFraction $tone={schedAtLimit ? 'amber' : 'default'}>
                  {usedGroups} / {g || '—'}
                </UsageFraction>
              )}
            </UsageMetricTop>
            <BarTrack>
              {!locked && g > 0 ? (
                <BarFill
                  $pct={pct(usedGroups, g)}
                  $tone={schedAtLimit ? 'amber' : 'default'}
                />
              ) : null}
            </BarTrack>
            {showUsageGuidance && !locked && g > 0 ? (
              schedAtLimit ? (
                <UsageHintWarn>
                  {t('profileSubscription.billing.usageAtLimitSchedulers')}
                </UsageHintWarn>
              ) : (
                <UsageHintSmall>
                  {t('profileSubscription.billing.usageSchedulersRemaining', {
                    count: Math.max(0, g - usedGroups),
                  })}
                </UsageHintSmall>
              )
            ) : null}
          </UsageMetric>
          <UsageMetric>
            <UsageMetricTop>
              <UsageLabel>
                {t('profileSubscription.billing.usageStorage')}
              </UsageLabel>
              {locked ? (
                <UsageFractionMuted>0.00 GB / —</UsageFractionMuted>
              ) : (
                <UsageFraction $tone={storageAtLimit ? 'amber' : 'default'}>
                  {usedStorage.toFixed(2)} GB / {s > 0 ? `${s} GB` : '—'}
                </UsageFraction>
              )}
            </UsageMetricTop>
            <BarTrack>
              {!locked && s > 0 ? (
                <BarFill
                  $pct={pct(usedStorage, s)}
                  $tone={storageAtLimit ? 'amber' : 'default'}
                />
              ) : null}
            </BarTrack>
            {showUsageGuidance && !locked && s > 0 ? (
              storageAtLimit ? (
                <UsageHintWarn>
                  {t('profileSubscription.billing.usageAtLimitStorage')}
                </UsageHintWarn>
              ) : (
                <UsageHintSmall>
                  {t('profileSubscription.billing.usageStorageRemaining', {
                    count: Math.max(0, Math.round((s - usedStorage) * 10) / 10),
                  })}
                </UsageHintSmall>
              )
            ) : null}
          </UsageMetric>
        </UsageGrid>
        {anyAtLimit ? (
          <UsageUpgradeNudge>
            <div style={{ flexShrink: 0, marginTop: 2 }}>
              <IconInfo />
            </div>
            <UsageUpgradeNudgeText>
              <strong>
                {onlyCommunitiesAtLimit
                  ? t('profileSubscription.billing.usageUpgradeNudgeTitle')
                  : t('profileSubscription.billing.usagePlanLimitNudgeTitle')}
              </strong>
              <span style={{ display: 'block', marginTop: 4 }}>
                {onlyCommunitiesAtLimit
                  ? t('profileSubscription.billing.usageUpgradeNudgeBody')
                  : t('profileSubscription.billing.usagePlanLimitNudgeBody')}
              </span>
            </UsageUpgradeNudgeText>
            <UsageUpgradeBtn as={Link} to={changePlanPath}>
              {t('profileSubscription.billing.usageUpgradeCta')}
            </UsageUpgradeBtn>
          </UsageUpgradeNudge>
        ) : null}
      </UsageCard>
    );
  };

  const freeAccountBody = () => (
    <>
      <HeroCard>
        <HeroAccent />
        <HeroInner>
          <HeroLeft>
            <IconBox $filled>
              <IconRocket />
            </IconBox>
            <div>
              <HeroTitle>
                {t('profileSubscription.billing.heroTitle')}
              </HeroTitle>
              <HeroSubtitle>
                {t('profileSubscription.billing.heroSubtitle')}
              </HeroSubtitle>
            </div>
          </HeroLeft>
          <HeroActions>
            <TextLink as={Link} to={changePlanPath}>
              {t('profileSubscription.billing.seeIncluded')}
            </TextLink>
            {!profile?.trialStarted ? (
              <PopupButtonMessage
                popupHeaderText={t('subscriptions.startTrialHeader')}
                popupMessageText={t('subscriptions.startTrialText')}
                popupButtonText={t('subscriptions.startTrialPopupButton')}
                ButtonComponent={TrialStartButton}
                onConfirm={handleTrialStart}
              />
            ) : null}
          </HeroActions>
        </HeroInner>
      </HeroCard>

      <TwoColGrid>
        <Card>
          <CardHeaderRow>
            <div>
              <CardTitle>
                {t('profileSubscription.billing.currentPlanTitle')}
              </CardTitle>
              <CardSubtitle>
                {t('profileSubscription.billing.noSubscriptionSubtitle')}
              </CardSubtitle>
            </div>
            <Badge $tone="slate">
              {t('profileSubscription.billing.freeAccountBadge')}
            </Badge>
          </CardHeaderRow>
          <PlanName $muted>
            {t('profileSubscription.billing.noActivePlan')}
          </PlanName>
          <FeatureBox>
            <FeatureBlurb>
              {t('profileSubscription.billing.trialUnlockBlurb')}
            </FeatureBlurb>
            <FeatureList>
              <FeatureItem>
                <IconCheck />
                {t('profileSubscription.billing.featureCommunities', {
                  count: TRIAL_DEFAULTS.maxCommunities,
                })}
              </FeatureItem>
              <FeatureItem>
                <IconCheck />
                {t('profileSubscription.billing.featureSchedulers', {
                  count: TRIAL_DEFAULTS.maxGroups,
                })}
              </FeatureItem>
              <FeatureItem>
                <IconCheck />
                {t('profileSubscription.billing.featureMembers', {
                  count: TRIAL_DEFAULTS.maxMembers,
                })}
              </FeatureItem>
              <FeatureItem>
                <IconCheck />
                {t('profileSubscription.billing.featureStorage', {
                  count: TRIAL_DEFAULTS.maxStorage,
                })}
              </FeatureItem>
            </FeatureList>
          </FeatureBox>
          <PlanFooter>
            <FooterNote>
              <IconCalendar />
              {t('profileSubscription.billing.trialNoteFooter')}
            </FooterNote>
            <ActionRow>
              {!profile?.trialStarted ? (
                <PopupButtonMessage
                  popupHeaderText={t('subscriptions.startTrialHeader')}
                  popupMessageText={t('subscriptions.startTrialText')}
                  popupButtonText={t('subscriptions.startTrialPopupButton')}
                  ButtonComponent={TrialStartButton}
                  onConfirm={handleTrialStart}
                />
              ) : null}
              <TextLink as={Link} to={changePlanPath}>
                {t('profileSubscription.billing.comparePlans')}
              </TextLink>
            </ActionRow>
          </PlanFooter>
        </Card>
        {paymentColumn}
      </TwoColGrid>

      {usageSection(true)}

      <TrustRow>{paymentTrustFooter}</TrustRow>
    </>
  );

  const subscribedBody = () => {
    if (!subscription?.id) return null;

    const paidProBullets = (
      t('profileSubscription.billing.paidProFeatureBullets', {
        returnObjects: true,
      }) as string[]
    ).filter(Boolean);

    const featureItems =
      isPaidActive && !isTrial ? (
        <FeatureList style={{ marginBottom: '28px' }}>
          {paidProBullets.map((line) => (
            <FeatureItem key={line}>
              <IconCheck />
              {line}
            </FeatureItem>
          ))}
        </FeatureList>
      ) : (
        <FeatureList style={{ marginBottom: '28px' }}>
          <FeatureItem>
            <IconCheck />
            {t('profileSubscription.billing.featureCommunities', {
              count: maxCommunities || TRIAL_DEFAULTS.maxCommunities,
            })}
          </FeatureItem>
          <FeatureItem>
            <IconCheck />
            {t('profileSubscription.billing.featureSchedulers', {
              count: maxGroups || TRIAL_DEFAULTS.maxGroups,
            })}
          </FeatureItem>
          <FeatureItem>
            <IconCheck />
            {t('profileSubscription.billing.featureMembers', {
              count: maxUsers || TRIAL_DEFAULTS.maxMembers,
            })}
          </FeatureItem>
          <FeatureItem>
            <IconCheck />
            {t('profileSubscription.billing.featureStorage', {
              count: maxBucketSize || TRIAL_DEFAULTS.maxStorage,
            })}
          </FeatureItem>
        </FeatureList>
      );

    let topBanner: React.ReactNode = null;
    if (isTrial) {
      topBanner = (
        <TrialBanner>
          <TrialBannerWarningLottie />
          <TrialBannerContent>
            <TrialBannerTitle>
              {t('profileSubscription.billing.trialBannerTitle')}
            </TrialBannerTitle>
            <TrialBannerText>
              {t('profileSubscription.billing.trialBannerBody', {
                date: subscriptionEndDate,
              })}
            </TrialBannerText>
          </TrialBannerContent>
        </TrialBanner>
      );
    } else if (isPaidActive) {
      const sp = subscription.subscriptionPlan;
      const cur = sp?.currency || 'USD';
      const renewalAmt =
        subscription.frequency === SubscriptionFrequency.YEAR
          ? sp?.yearlyValue ?? 0
          : sp?.monthlyValue ?? 0;
      const periodForBanner =
        subscription.frequency === SubscriptionFrequency.YEAR
          ? t('changePlan.perYear')
          : t('changePlan.perMonth');
      topBanner = (
        <StatusBanner $variant="success">
          <StatusBannerMain>
            <StatusCheckWrap>
              <IconCheck />
            </StatusCheckWrap>
            <StatusBannerTextCol>
              <StatusBannerTitle as="div" style={{ display: 'block' }}>
                {t('profileSubscription.billing.statusActiveLabel', {
                  plan: planName,
                })}
              </StatusBannerTitle>
              <StatusBannerBody
                as="div"
                style={{ marginLeft: 0, display: 'block', marginTop: 4 }}
              >
                {t('profileSubscription.billing.statusRenewalRich', {
                  date: subscriptionEndDate,
                  price: formatMoney(cur, renewalAmt),
                  period: periodForBanner,
                })}
              </StatusBannerBody>
            </StatusBannerTextCol>
          </StatusBannerMain>
          <FrequencyPill>
            {subscription.frequency === SubscriptionFrequency.YEAR
              ? t('profileSubscription.billing.frequencyAnnual')
              : t('profileSubscription.billing.frequencyMonthly')}
          </FrequencyPill>
        </StatusBanner>
      );
    } else if (isCancelled) {
      topBanner = (
        <StatusBanner $variant="muted">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ marginBottom: 4 }}>
              <StatusBannerTitle>
                {t('subscriptions.cancelledTitle')}
              </StatusBannerTitle>
            </div>
            <StatusBannerBody
              as="div"
              style={{ marginLeft: 0, display: 'block' }}
            >
              {t('profileSubscription.details.infoCancelled')}
            </StatusBannerBody>
          </div>
          <Badge $tone="slate">
            {t('profileSubscription.billing.cancelledBadge')}
          </Badge>
        </StatusBanner>
      );
    }

    const planSubtitle = isTrial
      ? t('profileSubscription.billing.currentPlanSubtitleTrial')
      : isCancelled
      ? `${t('profileSubscription.details.validUntil')} ${subscriptionEndDate}`
      : t('profileSubscription.billing.paidPlanSubtitle');

    const badge = isTrial ? (
      <Badge $tone="teal">{t('profileSubscription.billing.trialBadge')}</Badge>
    ) : isCancelled ? (
      <Badge $tone="slate">
        {t('profileSubscription.billing.cancelledBadge')}
      </Badge>
    ) : isPaidActive ? (
      <Badge $tone="teal">
        {planName} · {t('profileSubscription.billing.activeBadge')}
      </Badge>
    ) : (
      <Badge $tone="teal">{t('profileSubscription.billing.activeBadge')}</Badge>
    );

    const footerNote = isTrial ? (
      <FooterNote>
        <IconCalendar />
        {t('profileSubscription.billing.endsOnNote', {
          date: subscriptionEndDate,
        })}
      </FooterNote>
    ) : isPaidActive && subscription.subscriptionPlan ? (
      <FooterNote>
        <IconCalendar />
        {t('profileSubscription.billing.renewalPaymentFootnote', {
          date: subscriptionEndDate,
          price: formatMoney(
            subscription.subscriptionPlan.currency,
            subscription.frequency === SubscriptionFrequency.YEAR
              ? subscription.subscriptionPlan.yearlyValue
              : subscription.subscriptionPlan.monthlyValue,
          ),
          period:
            subscription.frequency === SubscriptionFrequency.YEAR
              ? t('changePlan.perYear')
              : t('changePlan.perMonth'),
          chargedTo:
            paymentMethodLine ||
            t('profileSubscription.billing.paymentMethodChargedGeneric'),
        })}
      </FooterNote>
    ) : (
      <FooterNote>
        <IconCalendar />
        {nextPaymentLabel} {subscriptionEndDate}
      </FooterNote>
    );

    const planActions = (() => {
      if (isCancelled) {
        return (
          <PopupEventWrapper
            popupMessageText={t('subscriptions.renewSubscriptionText')}
            popupButtons={[
              {
                popupButtonText: t('subscriptions.activate'),
                handleConfirm: () => {
                  if (subscription.subscriptionId) {
                    dispatch(activateSubscription(subscription.subscriptionId));
                  }
                },
              },
            ]}
            darkBackground
          >
            <BtnPrimary type="button">
              {t('profileSubscription.buttonRestart')}
            </BtnPrimary>
          </PopupEventWrapper>
        );
      }
      if (isTrial) {
        return (
          <ActionRow>
            <ChoosePlanFullButton as={Link} to={changePlanPath}>
              {t('profileSubscription.billing.choosePlan')}
              <IconChevronRight />
            </ChoosePlanFullButton>
          </ActionRow>
        );
      }
      return (
        <ActionRow>
          <SplitButtonWrap ref={planMenuRef}>
            <SplitButtonMain as={Link} to={changePlanPath}>
              {t('profileSubscription.billing.changePlan')}
            </SplitButtonMain>
            <SplitButtonChevron
              type="button"
              aria-expanded={planMenuOpen}
              aria-label="Plan options"
              onClick={(e) => {
                e.stopPropagation();
                setPlanMenuOpen((o) => !o);
              }}
            >
              <IconChevronDown />
            </SplitButtonChevron>
            <DropdownPanel $open={planMenuOpen}>
              <DropdownItem
                as={Link}
                to={changePlanPath}
                onClick={() => setPlanMenuOpen(false)}
              >
                {t('profileSubscription.billing.choosePlan')}
              </DropdownItem>
              <DropdownItem
                as={Link}
                to={changePlanPath}
                onClick={() => setPlanMenuOpen(false)}
              >
                {t('profileSubscription.billing.comparePlansMenu')}
              </DropdownItem>
            </DropdownPanel>
          </SplitButtonWrap>
        </ActionRow>
      );
    })();

    return (
      <>
        {topBanner}
        <TwoColGrid>
          <Card>
            <CardHeaderRow>
              <div>
                <CardTitle>
                  {t('profileSubscription.billing.currentPlanTitle')}
                </CardTitle>
                <CardSubtitle>{planSubtitle}</CardSubtitle>
              </div>
              {badge}
            </CardHeaderRow>
            <PlanName $muted={isCancelled}>{planName}</PlanName>
            {featureItems}
            <PlanFooter>
              {footerNote}
              {planActions}
            </PlanFooter>
          </Card>
          {paymentColumn}
        </TwoColGrid>
        {usageSection(false, true)}
        <TrustRow>{paymentTrustFooter}</TrustRow>
      </>
    );
  };

  return (
    <ContentWrapper minHeight="100svh">
      <PageRoot>
        {isLoading && subscription?.id && (
          <FloatingLoaderWrap>
            <Loader size="60px" type={null} />
          </FloatingLoaderWrap>
        )}
        <TopBar>
          <TopBarTitle>
            {t('profileSubscription.billing.pageTitle')}
          </TopBarTitle>
        </TopBar>
        <Main>
          {stripeOutcome ? (
            <StripeCheckoutOutcome
              variant={stripeOutcome}
              sessionId={searchParams.get('session_id')}
              planLabel={checkoutPlanLabel}
              onDismiss={clearStripeQuery}
            />
          ) : showInitialLoader ? (
            <Loader />
          ) : !subscription?.id ? (
            freeAccountBody()
          ) : (
            subscribedBody()
          )}
        </Main>
      </PageRoot>
    </ContentWrapper>
  );
};

export default UserProfileSubscription;
